import { LitElement as G, css as Z, html as W, nothing as Ae } from "lit";
import { state as b, queryAssignedElements as ue, property as Ee, query as ge } from "lit/decorators.js";
import { classMap as z } from "lit/directives/class-map.js";
import { property as _e } from "lit/decorators/property.js";
/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const N = globalThis, J = N.ShadowRoot && (N.ShadyCSS === void 0 || N.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, fe = Symbol(), Q = /* @__PURE__ */ new WeakMap();
let Se = class {
  constructor(s, e, t) {
    if (this._$cssResult$ = !0, t !== fe) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = s, this.t = e;
  }
  get styleSheet() {
    let s = this.o;
    const e = this.t;
    if (J && s === void 0) {
      const t = e !== void 0 && e.length === 1;
      t && (s = Q.get(e)), s === void 0 && ((this.o = s = new CSSStyleSheet()).replaceSync(this.cssText), t && Q.set(e, s));
    }
    return s;
  }
  toString() {
    return this.cssText;
  }
};
const Ce = (s) => new Se(typeof s == "string" ? s : s + "", void 0, fe), Oe = (s, e) => {
  if (J) s.adoptedStyleSheets = e.map((t) => t instanceof CSSStyleSheet ? t : t.styleSheet);
  else for (const t of e) {
    const i = document.createElement("style"), o = N.litNonce;
    o !== void 0 && i.setAttribute("nonce", o), i.textContent = t.cssText, s.appendChild(i);
  }
}, ee = J ? (s) => s : (s) => s instanceof CSSStyleSheet ? ((e) => {
  let t = "";
  for (const i of e.cssRules) t += i.cssText;
  return Ce(t);
})(s) : s;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: ke, defineProperty: ze, getOwnPropertyDescriptor: Te, getOwnPropertyNames: Pe, getOwnPropertySymbols: Re, getPrototypeOf: He } = Object, S = globalThis, te = S.trustedTypes, Me = te ? te.emptyScript : "", ie = S.reactiveElementPolyfillSupport, T = (s, e) => s, q = { toAttribute(s, e) {
  switch (e) {
    case Boolean:
      s = s ? Me : null;
      break;
    case Object:
    case Array:
      s = s == null ? s : JSON.stringify(s);
  }
  return s;
}, fromAttribute(s, e) {
  let t = s;
  switch (e) {
    case Boolean:
      t = s !== null;
      break;
    case Number:
      t = s === null ? null : Number(s);
      break;
    case Object:
    case Array:
      try {
        t = JSON.parse(s);
      } catch {
        t = null;
      }
  }
  return t;
} }, ve = (s, e) => !ke(s, e), oe = { attribute: !0, type: String, converter: q, reflect: !1, hasChanged: ve };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), S.litPropertyMetadata ?? (S.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
class E extends HTMLElement {
  static addInitializer(e) {
    this._$Ei(), (this.l ?? (this.l = [])).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, t = oe) {
    if (t.state && (t.attribute = !1), this._$Ei(), this.elementProperties.set(e, t), !t.noAccessor) {
      const i = Symbol(), o = this.getPropertyDescriptor(e, i, t);
      o !== void 0 && ze(this.prototype, e, o);
    }
  }
  static getPropertyDescriptor(e, t, i) {
    const { get: o, set: r } = Te(this.prototype, e) ?? { get() {
      return this[t];
    }, set(n) {
      this[t] = n;
    } };
    return { get() {
      return o == null ? void 0 : o.call(this);
    }, set(n) {
      const d = o == null ? void 0 : o.call(this);
      r.call(this, n), this.requestUpdate(e, d, i);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) ?? oe;
  }
  static _$Ei() {
    if (this.hasOwnProperty(T("elementProperties"))) return;
    const e = He(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(T("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(T("properties"))) {
      const t = this.properties, i = [...Pe(t), ...Re(t)];
      for (const o of i) this.createProperty(o, t[o]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const t = litPropertyMetadata.get(e);
      if (t !== void 0) for (const [i, o] of t) this.elementProperties.set(i, o);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [t, i] of this.elementProperties) {
      const o = this._$Eu(t, i);
      o !== void 0 && this._$Eh.set(o, t);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const t = [];
    if (Array.isArray(e)) {
      const i = new Set(e.flat(1 / 0).reverse());
      for (const o of i) t.unshift(ee(o));
    } else e !== void 0 && t.push(ee(e));
    return t;
  }
  static _$Eu(e, t) {
    const i = t.attribute;
    return i === !1 ? void 0 : typeof i == "string" ? i : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((t) => t(this));
  }
  addController(e) {
    var t;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(e), this.renderRoot !== void 0 && this.isConnected && ((t = e.hostConnected) == null || t.call(e));
  }
  removeController(e) {
    var t;
    (t = this._$EO) == null || t.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), t = this.constructor.elementProperties;
    for (const i of t.keys()) this.hasOwnProperty(i) && (e.set(i, this[i]), delete this[i]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    const e = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return Oe(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var e;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$EO) == null || e.forEach((t) => {
      var i;
      return (i = t.hostConnected) == null ? void 0 : i.call(t);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((t) => {
      var i;
      return (i = t.hostDisconnected) == null ? void 0 : i.call(t);
    });
  }
  attributeChangedCallback(e, t, i) {
    this._$AK(e, i);
  }
  _$EC(e, t) {
    var i;
    const o = this.constructor.elementProperties.get(e), r = this.constructor._$Eu(e, o);
    if (r !== void 0 && o.reflect === !0) {
      const n = (((i = o.converter) == null ? void 0 : i.toAttribute) !== void 0 ? o.converter : q).toAttribute(t, o.type);
      this._$Em = e, n == null ? this.removeAttribute(r) : this.setAttribute(r, n), this._$Em = null;
    }
  }
  _$AK(e, t) {
    var i;
    const o = this.constructor, r = o._$Eh.get(e);
    if (r !== void 0 && this._$Em !== r) {
      const n = o.getPropertyOptions(r), d = typeof n.converter == "function" ? { fromAttribute: n.converter } : ((i = n.converter) == null ? void 0 : i.fromAttribute) !== void 0 ? n.converter : q;
      this._$Em = r, this[r] = d.fromAttribute(t, n.type), this._$Em = null;
    }
  }
  requestUpdate(e, t, i) {
    if (e !== void 0) {
      if (i ?? (i = this.constructor.getPropertyOptions(e)), !(i.hasChanged ?? ve)(this[e], t)) return;
      this.P(e, t, i);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$ET());
  }
  P(e, t, i) {
    this._$AL.has(e) || this._$AL.set(e, t), i.reflect === !0 && this._$Em !== e && (this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Set())).add(e);
  }
  async _$ET() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (t) {
      Promise.reject(t);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var e;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [r, n] of this._$Ep) this[r] = n;
        this._$Ep = void 0;
      }
      const o = this.constructor.elementProperties;
      if (o.size > 0) for (const [r, n] of o) n.wrapped !== !0 || this._$AL.has(r) || this[r] === void 0 || this.P(r, this[r], n);
    }
    let t = !1;
    const i = this._$AL;
    try {
      t = this.shouldUpdate(i), t ? (this.willUpdate(i), (e = this._$EO) == null || e.forEach((o) => {
        var r;
        return (r = o.hostUpdate) == null ? void 0 : r.call(o);
      }), this.update(i)) : this._$EU();
    } catch (o) {
      throw t = !1, this._$EU(), o;
    }
    t && this._$AE(i);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var t;
    (t = this._$EO) == null || t.forEach((i) => {
      var o;
      return (o = i.hostUpdated) == null ? void 0 : o.call(i);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EU() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Ej && (this._$Ej = this._$Ej.forEach((t) => this._$EC(t, this[t]))), this._$EU();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
}
E.elementStyles = [], E.shadowRootOptions = { mode: "open" }, E[T("elementProperties")] = /* @__PURE__ */ new Map(), E[T("finalized")] = /* @__PURE__ */ new Map(), ie == null || ie({ ReactiveElement: E }), (S.reactiveElementVersions ?? (S.reactiveElementVersions = [])).push("2.0.4");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const D = globalThis, I = D.trustedTypes, se = I ? I.createPolicy("lit-html", { createHTML: (s) => s }) : void 0, xe = "$lit$", f = `lit$${Math.random().toFixed(9).slice(2)}$`, ye = "?" + f, Ue = `<${ye}>`, w = document, P = () => w.createComment(""), R = (s) => s === null || typeof s != "object" && typeof s != "function", K = Array.isArray, Le = (s) => K(s) || typeof (s == null ? void 0 : s[Symbol.iterator]) == "function", V = `[ 	
\f\r]`, k = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, ne = /-->/g, re = />/g, y = RegExp(`>|${V}(?:([^\\s"'>=/]+)(${V}*=${V}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), ae = /'/g, le = /"/g, $e = /^(?:script|style|textarea|title)$/i, C = Symbol.for("lit-noChange"), p = Symbol.for("lit-nothing"), de = /* @__PURE__ */ new WeakMap(), $ = w.createTreeWalker(w, 129);
function we(s, e) {
  if (!K(s) || !s.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return se !== void 0 ? se.createHTML(e) : e;
}
const Ne = (s, e) => {
  const t = s.length - 1, i = [];
  let o, r = e === 2 ? "<svg>" : e === 3 ? "<math>" : "", n = k;
  for (let d = 0; d < t; d++) {
    const a = s[d];
    let h, c, l = -1, u = 0;
    for (; u < a.length && (n.lastIndex = u, c = n.exec(a), c !== null); ) u = n.lastIndex, n === k ? c[1] === "!--" ? n = ne : c[1] !== void 0 ? n = re : c[2] !== void 0 ? ($e.test(c[2]) && (o = RegExp("</" + c[2], "g")), n = y) : c[3] !== void 0 && (n = y) : n === y ? c[0] === ">" ? (n = o ?? k, l = -1) : c[1] === void 0 ? l = -2 : (l = n.lastIndex - c[2].length, h = c[1], n = c[3] === void 0 ? y : c[3] === '"' ? le : ae) : n === le || n === ae ? n = y : n === ne || n === re ? n = k : (n = y, o = void 0);
    const m = n === y && s[d + 1].startsWith("/>") ? " " : "";
    r += n === k ? a + Ue : l >= 0 ? (i.push(h), a.slice(0, l) + xe + a.slice(l) + f + m) : a + f + (l === -2 ? d : m);
  }
  return [we(s, r + (s[t] || "<?>") + (e === 2 ? "</svg>" : e === 3 ? "</math>" : "")), i];
};
class H {
  constructor({ strings: e, _$litType$: t }, i) {
    let o;
    this.parts = [];
    let r = 0, n = 0;
    const d = e.length - 1, a = this.parts, [h, c] = Ne(e, t);
    if (this.el = H.createElement(h, i), $.currentNode = this.el.content, t === 2 || t === 3) {
      const l = this.el.content.firstChild;
      l.replaceWith(...l.childNodes);
    }
    for (; (o = $.nextNode()) !== null && a.length < d; ) {
      if (o.nodeType === 1) {
        if (o.hasAttributes()) for (const l of o.getAttributeNames()) if (l.endsWith(xe)) {
          const u = c[n++], m = o.getAttribute(l).split(f), A = /([.?@])?(.*)/.exec(u);
          a.push({ type: 1, index: r, name: A[2], strings: m, ctor: A[1] === "." ? We : A[1] === "?" ? De : A[1] === "@" ? Ie : B }), o.removeAttribute(l);
        } else l.startsWith(f) && (a.push({ type: 6, index: r }), o.removeAttribute(l));
        if ($e.test(o.tagName)) {
          const l = o.textContent.split(f), u = l.length - 1;
          if (u > 0) {
            o.textContent = I ? I.emptyScript : "";
            for (let m = 0; m < u; m++) o.append(l[m], P()), $.nextNode(), a.push({ type: 2, index: ++r });
            o.append(l[u], P());
          }
        }
      } else if (o.nodeType === 8) if (o.data === ye) a.push({ type: 2, index: r });
      else {
        let l = -1;
        for (; (l = o.data.indexOf(f, l + 1)) !== -1; ) a.push({ type: 7, index: r }), l += f.length - 1;
      }
      r++;
    }
  }
  static createElement(e, t) {
    const i = w.createElement("template");
    return i.innerHTML = e, i;
  }
}
function O(s, e, t = s, i) {
  var o, r;
  if (e === C) return e;
  let n = i !== void 0 ? (o = t.o) == null ? void 0 : o[i] : t.l;
  const d = R(e) ? void 0 : e._$litDirective$;
  return (n == null ? void 0 : n.constructor) !== d && ((r = n == null ? void 0 : n._$AO) == null || r.call(n, !1), d === void 0 ? n = void 0 : (n = new d(s), n._$AT(s, t, i)), i !== void 0 ? (t.o ?? (t.o = []))[i] = n : t.l = n), n !== void 0 && (e = O(s, n._$AS(s, e.values), n, i)), e;
}
class je {
  constructor(e, t) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = t;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    const { el: { content: t }, parts: i } = this._$AD, o = ((e == null ? void 0 : e.creationScope) ?? w).importNode(t, !0);
    $.currentNode = o;
    let r = $.nextNode(), n = 0, d = 0, a = i[0];
    for (; a !== void 0; ) {
      if (n === a.index) {
        let h;
        a.type === 2 ? h = new U(r, r.nextSibling, this, e) : a.type === 1 ? h = new a.ctor(r, a.name, a.strings, this, e) : a.type === 6 && (h = new Be(r, this, e)), this._$AV.push(h), a = i[++d];
      }
      n !== (a == null ? void 0 : a.index) && (r = $.nextNode(), n++);
    }
    return $.currentNode = w, o;
  }
  p(e) {
    let t = 0;
    for (const i of this._$AV) i !== void 0 && (i.strings !== void 0 ? (i._$AI(e, i, t), t += i.strings.length - 2) : i._$AI(e[t])), t++;
  }
}
class U {
  get _$AU() {
    var e;
    return ((e = this._$AM) == null ? void 0 : e._$AU) ?? this.v;
  }
  constructor(e, t, i, o) {
    this.type = 2, this._$AH = p, this._$AN = void 0, this._$AA = e, this._$AB = t, this._$AM = i, this.options = o, this.v = (o == null ? void 0 : o.isConnected) ?? !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const t = this._$AM;
    return t !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = t.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, t = this) {
    e = O(this, e, t), R(e) ? e === p || e == null || e === "" ? (this._$AH !== p && this._$AR(), this._$AH = p) : e !== this._$AH && e !== C && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : Le(e) ? this.k(e) : this._(e);
  }
  O(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  _(e) {
    this._$AH !== p && R(this._$AH) ? this._$AA.nextSibling.data = e : this.T(w.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var t;
    const { values: i, _$litType$: o } = e, r = typeof o == "number" ? this._$AC(e) : (o.el === void 0 && (o.el = H.createElement(we(o.h, o.h[0]), this.options)), o);
    if (((t = this._$AH) == null ? void 0 : t._$AD) === r) this._$AH.p(i);
    else {
      const n = new je(r, this), d = n.u(this.options);
      n.p(i), this.T(d), this._$AH = n;
    }
  }
  _$AC(e) {
    let t = de.get(e.strings);
    return t === void 0 && de.set(e.strings, t = new H(e)), t;
  }
  k(e) {
    K(this._$AH) || (this._$AH = [], this._$AR());
    const t = this._$AH;
    let i, o = 0;
    for (const r of e) o === t.length ? t.push(i = new U(this.O(P()), this.O(P()), this, this.options)) : i = t[o], i._$AI(r), o++;
    o < t.length && (this._$AR(i && i._$AB.nextSibling, o), t.length = o);
  }
  _$AR(e = this._$AA.nextSibling, t) {
    var i;
    for ((i = this._$AP) == null ? void 0 : i.call(this, !1, !0, t); e && e !== this._$AB; ) {
      const o = e.nextSibling;
      e.remove(), e = o;
    }
  }
  setConnected(e) {
    var t;
    this._$AM === void 0 && (this.v = e, (t = this._$AP) == null || t.call(this, e));
  }
}
class B {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, t, i, o, r) {
    this.type = 1, this._$AH = p, this._$AN = void 0, this.element = e, this.name = t, this._$AM = o, this.options = r, i.length > 2 || i[0] !== "" || i[1] !== "" ? (this._$AH = Array(i.length - 1).fill(new String()), this.strings = i) : this._$AH = p;
  }
  _$AI(e, t = this, i, o) {
    const r = this.strings;
    let n = !1;
    if (r === void 0) e = O(this, e, t, 0), n = !R(e) || e !== this._$AH && e !== C, n && (this._$AH = e);
    else {
      const d = e;
      let a, h;
      for (e = r[0], a = 0; a < r.length - 1; a++) h = O(this, d[i + a], t, a), h === C && (h = this._$AH[a]), n || (n = !R(h) || h !== this._$AH[a]), h === p ? e = p : e !== p && (e += (h ?? "") + r[a + 1]), this._$AH[a] = h;
    }
    n && !o && this.j(e);
  }
  j(e) {
    e === p ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}
class We extends B {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === p ? void 0 : e;
  }
}
class De extends B {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== p);
  }
}
class Ie extends B {
  constructor(e, t, i, o, r) {
    super(e, t, i, o, r), this.type = 5;
  }
  _$AI(e, t = this) {
    if ((e = O(this, e, t, 0) ?? p) === C) return;
    const i = this._$AH, o = e === p && i !== p || e.capture !== i.capture || e.once !== i.once || e.passive !== i.passive, r = e !== p && (i === p || o);
    o && this.element.removeEventListener(this.name, this, i), r && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var t;
    typeof this._$AH == "function" ? this._$AH.call(((t = this.options) == null ? void 0 : t.host) ?? this.element, e) : this._$AH.handleEvent(e);
  }
}
class Be {
  constructor(e, t, i) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = t, this.options = i;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    O(this, e);
  }
}
const he = D.litHtmlPolyfillSupport;
he == null || he(H, U), (D.litHtmlVersions ?? (D.litHtmlVersions = [])).push("3.2.0");
const Fe = (s, e, t) => {
  const i = (t == null ? void 0 : t.renderBefore) ?? e;
  let o = i._$litPart$;
  if (o === void 0) {
    const r = (t == null ? void 0 : t.renderBefore) ?? null;
    i._$litPart$ = o = new U(e.insertBefore(P(), r), r, void 0, t ?? {});
  }
  return o._$AI(s), o;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class j extends E {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this.o = void 0;
  }
  createRenderRoot() {
    var e;
    const t = super.createRenderRoot();
    return (e = this.renderOptions).renderBefore ?? (e.renderBefore = t.firstChild), t;
  }
  update(e) {
    const t = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this.o = Fe(t, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this.o) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this.o) == null || e.setConnected(!1);
  }
  render() {
    return C;
  }
}
var ce;
j._$litElement$ = !0, j.finalized = !0, (ce = globalThis.litElementHydrateSupport) == null || ce.call(globalThis, { LitElement: j });
const pe = globalThis.litElementPolyfillSupport;
pe == null || pe({ LitElement: j });
(globalThis.litElementVersions ?? (globalThis.litElementVersions = [])).push("4.1.0");
function Y(s, e, t) {
  const i = t ? `${e}-${t}` : e;
  customElements.get(i) ? console.warn(
    `WARN: You are importing '${i}' more than once, which is not supported. Please remove the duplicated import.`
  ) : customElements.define(i, s);
}
function me(s) {
  var e;
  return parseFloat(((e = /[0-9.]*/g.exec(s)) == null ? void 0 : e[0]) || "0");
}
function Ve(s, e) {
  let t = 0;
  return function(...i) {
    const o = Date.now();
    o - t >= e && (s.apply(this, i), t = o);
  };
}
const Ye = '.header{display:flex;position:sticky;top:0;flex-direction:column;z-index:var(--muse-hotfix-header-z-index)}.header__main{--content-height: 44px;display:grid;flex-grow:1;grid-template-areas:"logo search menu";grid-template-columns:max-content minmax(max-content,1fr) max-content;align-items:center;justify-content:space-between;transition:margin .3s ease;margin-inline:20px;padding:8px 0;max-width:1200px}@media (768px <= width){.header__main{--content-height: 70px;column-gap:24px;margin-inline:64px;padding:24px 0}}@media (1440px <= width){.header__main{justify-content:center;padding:24px 0}}.header__main-wrapper{display:flex;justify-content:center;box-shadow:0 10px 10px #00000008;background:#fff}.header__meta-area{display:flex;flex-grow:1;grid-area:meta;justify-content:space-between;transition:margin .3s ease;margin-inline:20px;max-width:1200px}@media (768px <= width){.header__meta-area{margin-inline:64px}}.header__meta-area-wrapper{display:flex;justify-content:center;background:#f1f4f8}.header__menu-area{display:flex;grid-area:menu;gap:16px;align-items:center;justify-self:start;transition:height .3s ease;min-height:var(--content-height)}@media (768px <= width){.header__menu-area{gap:24px}}.header__search-area{display:none;grid-area:search;align-items:center;justify-self:end;width:100%;max-width:720px;min-height:var(--content-height)}@media (1024px <= width){.header__search-area{display:flex;grid-area:search}}.header__search-toggle{--icon-size: 24px}@media (768px <= width){.header__search-toggle{--icon-size: 32px}}@media (1024px <= width){.header__search-toggle{display:none}}::slotted([slot=logo]){grid-area:logo;justify-self:start;transition:height .3s ease,margin .3s ease;height:var(--content-height)}@media (1024px <= width){::slotted([slot=logo]){margin-right:148px}}@media (1440px <= width){::slotted([slot=logo]){margin-right:168px}}::slotted([slot=search]){flex-grow:1}::slotted([slot=menu-area]){--icon-size: 24px}@media (768px <= width){::slotted([slot=menu-area]){--icon-size: 32px}}@media (1024px > width){.header--search-open .header__search-area{display:flex;max-width:unset}}@media (1024px > width){.header--search-open .header__main{grid-template-areas:"search search menu";gap:12px}}@media (1024px > width) and (768px <= width){.header--search-open .header__main{gap:24px}}@media (1024px > width){.header--search-open ::slotted([slot=logo]){display:none}}@media (1024px > width){.header--search-open ::slotted([slot=menu-area]){display:none}}.header--collapsed ::slotted([slot=logo]){grid-area:logo;justify-self:start;transition:height .3s ease,margin .3s ease;height:var(--content-height)}@media (1024px <= width){.header--collapsed ::slotted([slot=logo]){margin-right:148px}}@media (1440px <= width){.header--collapsed ::slotted([slot=logo]){margin-right:168px}}.header--collapsed .header__main{--content-height: 44px;display:grid;flex-grow:1;grid-template-areas:"logo search menu";grid-template-columns:max-content minmax(max-content,1fr) max-content;align-items:center;justify-content:space-between;transition:margin .3s ease;margin-inline:20px;padding:8px 0;max-width:1200px}@media (768px <= width){.header--collapsed .header__main{--content-height: 54px;column-gap:24px;margin-inline:64px;padding:12px 0}}@media (1440px <= width){.header--collapsed .header__main{justify-content:center;padding:16px 0}}';
var qe = Object.defineProperty, F = (s, e, t, i) => {
  for (var o = void 0, r = s.length - 1, n; r >= 0; r--)
    (n = s[r]) && (o = n(e, t, o) || o);
  return o && qe(e, t, o), o;
};
const Ge = "muse-header";
var v;
const L = (v = class extends G {
  constructor() {
    super(...arguments), this._isMobileSearchOpen = !1, this._hasSearch = !1, this._isCollapsed = !1, this._prevScrollY = (window == null ? void 0 : window.scrollY) ?? 0, this._isRezising = !1, this._resizeTimeout = void 0, this._handleMobileToggleClick = () => {
      this._toggleMobileSearch();
    }, this._handleEscape = (e) => {
      this._isMobileSearchOpen && e.key === "Escape" && this._toggleMobileSearch(!1);
    }, this._handleResize = () => {
      this._isRezising = !0, clearTimeout(this._resizeTimeout), this._resizeTimeout = setTimeout(() => this._isRezising = !1, 500);
    }, this._handleScroll = Ve(() => {
      const e = window.scrollY, t = this._prevScrollY, i = e - t > 0;
      this._prevScrollY = e, !this._isRezising && (e > 20 ? (e + 80 < t && !i && (this._isCollapsed = !1), e > t && i && (this._isCollapsed = !0)) : this._isCollapsed = !1);
    }, v.THROTTLE_MS), this._handleHashChange = (e) => {
      const t = new URL(e.newURL).hash.substring(1), i = this._searchAreaElements.find(({ id: o }) => o === t);
      i && (this._toggleMobileSearch(!0), i.focus());
    };
  }
  //#region -- Base
  /**
   * @ignore
   */
  static get styles() {
    return Z([Ye]);
  }
  //#endregion
  //#region -- Lifecycles
  /** @ignore */
  connectedCallback() {
    super.connectedCallback(), document.addEventListener("keyup", this._handleEscape), window == null || window.addEventListener("resize", this._handleResize), window == null || window.addEventListener("scroll", this._handleScroll, {
      passive: !0
    }), window == null || window.addEventListener("hashchange", this._handleHashChange);
  }
  /** @ignore */
  disconnectedCallback() {
    document.removeEventListener("keyup", this._handleEscape), window == null || window.removeEventListener("resize", this._handleResize), window == null || window.removeEventListener("scroll", this._handleScroll), window == null || window.removeEventListener("hashchange", this._handleHashChange), super.disconnectedCallback();
  }
  //#endregion
  //#region -- Methods
  /**
   * Open or close the mobile search depending on the current state or the force parameter
   *
   * @param {boolean} [force] forces a specific value
   *
   * @example
   * //opens the search disregarding the current open state
   * this._toggleMobileSearch(true)
   *
   * @example
   * //closes the search disregarding the current open state
   * this._toggleMobileSearch(false)
   *
   * @example
   * //opens or closes the search depending on the current open state
   * this._toggleMobileSearch()
   *
   * @ignore
   */
  _toggleMobileSearch(e = !this._isMobileSearchOpen) {
    this._isMobileSearchOpen = e;
  }
  /**
   * @ignore
   */
  _handleSearchSlotChange() {
    this._hasSearch = this._searchAreaElements.length > 0;
  }
  //#endregion
  //#region -- Render
  /**
   * @ignore
   */
  render() {
    const e = z({
      header: !0,
      "header--search-open": this._isMobileSearchOpen,
      "header--collapsed": this._isCollapsed
    });
    return W`
      <header class=${e}>
        <div class="header__meta-area-wrapper">
          <div class="header__meta-area">
            <slot name="meta-area"></slot>
          </div>
        </div>

        <div class="header__main-wrapper">
          <div class="header__main">
            <slot name="logo"></slot>

            <div class="header__search-area">
              <slot
                name="search"
                @slotchange=${this._handleSearchSlotChange}
              ></slot>
            </div>

            <div class="header__menu-area">
              ${this._hasSearch ? W`
                    <muse-minimal-icon-button
                      class="header__search-toggle"
                      variant="dark"
                      slot="search-toggle"
                      title="search"
                      @click="${this._handleMobileToggleClick}"
                    >
                      <muse-icon
                        name="${this._isMobileSearchOpen ? "close" : "search"}"
                      ></muse-icon>
                    </muse-minimal-icon-button>
                  ` : Ae}

              <slot name="menu-area"></slot>
            </div>
          </div>
        </div>
      </header>
    `;
  }
  //#endregion
}, v.shadowRootOptions = {
  mode: "open",
  delegatesFocus: !0
}, v.THROTTLE_MS = 100, v);
F([
  b()
], L.prototype, "_isMobileSearchOpen");
F([
  b()
], L.prototype, "_hasSearch");
F([
  b()
], L.prototype, "_isCollapsed");
F([
  ue({ slot: "search" })
], L.prototype, "_searchAreaElements");
let Ze = L;
const Je = ":host{width:max-content}.meta-item__link{display:inline-flex;gap:8px;align-items:center;padding:0;width:max-content;text-decoration-color:currentcolor;text-decoration-line:var(--meta-item-text-decoration-line, none);text-decoration-style:var(--meta-item-text-decoration-style, none);color:var(--meta-item-color-text, #356799)}.meta-item__link:focus-visible{outline:dashed 2px var(--meta-item-color-focus-outline, #7194b7);border-radius:4px}.meta-item__link:hover{text-decoration-line:var(--meta-item-text-decoration-line-hover, underline);text-decoration-style:var(--meta-item-text-decoration-style-hover, underline)}.meta-item__link:active{text-decoration-line:var(--meta-item-text-decoration-line-active, underline);text-decoration-style:var(--meta-item-text-decoration-style-active, double)}.meta-item__content{line-height:calc(1.875 * var(--muse-base-unit, 1rem));font-family:Fira Sans,sans-serif;font-size:calc(1.25 * var(--muse-base-unit, 1rem));font-weight:400}@media (width < 768px){.meta-item__content{line-height:calc(1.5 * var(--muse-base-unit, 1rem));font-family:Fira Sans,sans-serif;font-size:calc(1 * var(--muse-base-unit, 1rem));font-weight:400}}:host([inverted]) .meta-item{--meta-item-color-text: #fff;--meta-item-color-focus-outline: #fff}::slotted([slot=icon-start]),::slotted([slot=icon-end]){--icon-size: 16px;display:inline-flex}@media (width >= 768px){::slotted([slot=icon-start]),::slotted([slot=icon-end]){--icon-size: 24px}}";
var Ke = Object.defineProperty, be = (s, e, t, i) => {
  for (var o = void 0, r = s.length - 1, n; r >= 0; r--)
    (n = s[r]) && (o = n(e, t, o) || o);
  return o && Ke(e, t, o), o;
};
const Xe = "muse-meta-item", X = class X extends G {
  constructor() {
    super(...arguments), this.inverted = !1;
  }
  //#region -- Base
  /**
   * @ignore
   */
  static get styles() {
    return Z([Je]);
  }
  //#endregion
  //#region -- DOM References
  //#endregion
  //#region -- Computed
  //#endregion
  //#region -- Lifecycles
  /**
   * @ignore
   */
  connectedCallback() {
    super.connectedCallback(), this.setAttribute("role", "listitem");
  }
  //#endregion
  //#region -- Methods
  //#endregion
  //#region -- Events
  //#endregion
  //#region -- Render
  /**
   * @ignore
   */
  render() {
    return W`
      <a class="meta-item__link" href=${this.href}>
        <slot name="icon-start"></slot>

        <span class="meta-item__content"><slot></slot></span>

        <slot name="icon-end"></slot>
      </a>
    `;
  }
  //#endregion
};
X.shadowRootOptions = {
  mode: "open",
  delegatesFocus: !0
};
let M = X;
be([
  _e()
], M.prototype, "href");
be([
  _e({ type: Boolean, reflect: !0 })
], M.prototype, "inverted");
const Qe = ":host{flex-grow:1}.meta-item-group__content-wrapper{--meta-item-color-text: #fff;--meta-item-color-focus-outline: #fff;--meta-item-text-decoration-line: underline;--meta-item-text-decoration-style: underline;--meta-item-text-decoration-style-hover: double;box-sizing:border-box;display:none;position:absolute;top:0;border-radius:0 0 4px 4px;background:#356799;padding:calc(var(--trigger-height)) 0 0;width:auto;min-width:var(--trigger-width);list-style:none}@media (width >= 1024px){.meta-item-group__content-wrapper{--meta-item-color-text: #356799;--meta-item-color-focus-outline: unset;--meta-item-text-decoration-line: unset;--meta-item-text-decoration-style: unset;--meta-item-text-decoration-style-hover: unset;display:flex;position:unset;gap:32px;margin-inline:-32px;background:unset;padding-block:12px;padding-inline:32px;width:100%}}.meta-item-group__open-indicator{--icon-size: 16px;display:inline-flex}@media (width >= 768px){.meta-item-group__open-indicator{--icon-size: 24px}}.meta-item-group__trigger-content{line-height:calc(1.875 * var(--muse-base-unit, 1rem));font-family:Fira Sans,sans-serif;font-size:calc(1.25 * var(--muse-base-unit, 1rem));font-weight:400;display:flex;gap:8px;align-items:center}@media (width < 768px){.meta-item-group__trigger-content{line-height:calc(1.5 * var(--muse-base-unit, 1rem));font-family:Fira Sans,sans-serif;font-size:calc(1 * var(--muse-base-unit, 1rem));font-weight:400}}.meta-item-group__trigger-content--open{--meta-item-color-focus-outline: #fff}.meta-item-group:not(.meta-item-group--collapsed){align-items:center}@media (width >= 1024px){.meta-item-group:not(.meta-item-group--collapsed){display:flex;align-items:stretch}}.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__trigger{--color-background: none;--color-text: #356799;--meta-item-text-decoration-style: none;--meta-item-text-decoration-style-hover: underline;--meta-item-text-decoration-style-active: double;display:inline-flex;gap:8px;align-items:center;padding:0;width:max-content;text-decoration-color:currentcolor;text-decoration-line:var(--meta-item-text-decoration-line, none);text-decoration-style:var(--meta-item-text-decoration-style, none);color:var(--meta-item-color-text, #356799);-webkit-appearance:none;-moz-appearance:none;appearance:none;margin-inline:-20px;outline:none;border:none;background:var(--color-background);cursor:pointer;padding-block:8px;padding-inline:20px;min-width:max-content;height:100%;color:var(--color-text)}.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__trigger:focus-visible{outline:dashed 2px var(--meta-item-color-focus-outline, #7194b7);border-radius:4px}.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__trigger:hover{text-decoration-line:var(--meta-item-text-decoration-line-hover, underline);text-decoration-style:var(--meta-item-text-decoration-style-hover, underline)}.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__trigger:active{text-decoration-line:var(--meta-item-text-decoration-line-active, underline);text-decoration-style:var(--meta-item-text-decoration-style-active, double)}.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__trigger:focus-visible{outline:none;border-radius:0}@media (width >= 768px){.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__trigger{padding-block:12px}}@media (width >= 1024px){.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__trigger{display:none}}.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__trigger--open{--color-background: #356799;--color-text: #fff;--meta-item-color-text: #fff;--meta-item-color-focus-outline: #fff;position:relative;z-index:4001}.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__content-wrapper{--meta-item-color-text: #fff;--meta-item-color-focus-outline: #fff;--meta-item-text-decoration-line: underline;--meta-item-text-decoration-style: underline;--meta-item-text-decoration-style-hover: double;box-sizing:border-box;display:none;position:absolute;top:0;border-radius:0 0 4px 4px;background:#356799;padding:calc(var(--trigger-height)) 0 0;width:auto;min-width:var(--trigger-width);list-style:none}@media (width >= 1024px){.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__content-wrapper{--meta-item-color-text: #356799;--meta-item-color-focus-outline: unset;--meta-item-text-decoration-line: unset;--meta-item-text-decoration-style: unset;--meta-item-text-decoration-style-hover: unset;display:flex;position:unset;gap:32px;margin-inline:-32px;background:unset;padding-block:12px;padding-inline:32px;width:100%}}@media (width >= 1024px){.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__content-wrapper--initializing ::slotted(:not([slot])){visibility:hidden}}@media (width < 1024px){.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__content-wrapper--open{display:flex;flex-direction:column;gap:24px;z-index:4000;margin-inline:-20px;max-width:100vw;max-height:100vh}}.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__content{display:flex;flex-direction:column;gap:24px;margin:0;padding:24px;overflow-y:auto}@media (width >= 1024px){.meta-item-group:not(.meta-item-group--collapsed) .meta-item-group__content{display:flex;flex-direction:row;padding:0;overflow:visible}}.meta-item-group.meta-item-group--collapsed{align-items:center}.meta-item-group.meta-item-group--collapsed .meta-item-group__trigger{--color-background: none;--color-text: #356799;--meta-item-text-decoration-style: none;--meta-item-text-decoration-style-hover: underline;--meta-item-text-decoration-style-active: double;display:inline-flex;gap:8px;align-items:center;padding:0;width:max-content;text-decoration-color:currentcolor;text-decoration-line:var(--meta-item-text-decoration-line, none);text-decoration-style:var(--meta-item-text-decoration-style, none);color:var(--meta-item-color-text, #356799);-webkit-appearance:none;-moz-appearance:none;appearance:none;margin-inline:-20px;outline:none;border:none;background:var(--color-background);cursor:pointer;padding-block:8px;padding-inline:20px;min-width:max-content;height:100%;color:var(--color-text)}.meta-item-group.meta-item-group--collapsed .meta-item-group__trigger:focus-visible{outline:dashed 2px var(--meta-item-color-focus-outline, #7194b7);border-radius:4px}.meta-item-group.meta-item-group--collapsed .meta-item-group__trigger:hover{text-decoration-line:var(--meta-item-text-decoration-line-hover, underline);text-decoration-style:var(--meta-item-text-decoration-style-hover, underline)}.meta-item-group.meta-item-group--collapsed .meta-item-group__trigger:active{text-decoration-line:var(--meta-item-text-decoration-line-active, underline);text-decoration-style:var(--meta-item-text-decoration-style-active, double)}.meta-item-group.meta-item-group--collapsed .meta-item-group__trigger:focus-visible{outline:none;border-radius:0}@media (width >= 768px){.meta-item-group.meta-item-group--collapsed .meta-item-group__trigger{padding-block:12px}}.meta-item-group.meta-item-group--collapsed .meta-item-group__trigger--open{--color-background: #356799;--color-text: #fff;--meta-item-color-text: #fff;--meta-item-color-focus-outline: #fff;position:relative;z-index:4001}.meta-item-group.meta-item-group--collapsed .meta-item-group__content-wrapper{--meta-item-color-text: #fff;--meta-item-color-focus-outline: #fff;--meta-item-text-decoration-line: underline;--meta-item-text-decoration-style: underline;--meta-item-text-decoration-style-hover: double;box-sizing:border-box;display:none;position:absolute;top:0;border-radius:0 0 4px 4px;background:#356799;padding:calc(var(--trigger-height)) 0 0;width:auto;min-width:var(--trigger-width);list-style:none}.meta-item-group.meta-item-group--collapsed .meta-item-group__content-wrapper--open{display:flex;flex-direction:column;gap:24px;z-index:4000;margin-inline:-20px;max-width:100vw;max-height:100vh}.meta-item-group.meta-item-group--collapsed .meta-item-group__content{display:flex;flex-direction:column;gap:24px;margin:0;padding:24px;overflow-y:auto}::slotted([slot=trigger-icon]){--icon-size: 16px;display:inline-flex}@media (width >= 768px){::slotted([slot=trigger-icon]){--icon-size: 24px}}.meta-item-group__trigger:focus-visible .meta-item-group__trigger-content{outline:dashed 2px var(--meta-item-color-focus-outline, #7194b7);border-radius:2px}";
function et({
  container: s,
  children: e,
  screenWidth: t,
  maxConsideredWidth: i
}) {
  e.forEach((m) => m.style.setProperty("display", "none"));
  const o = s == null ? void 0 : s.clientWidth;
  e.forEach((m) => m.style.removeProperty("display"));
  const r = e.reduce(
    (m, A) => m + A.clientWidth,
    0
  ), n = getComputedStyle(s), d = me(n.gap), a = me(n.paddingInline) * 2, c = d * (e.length - 1) + r + a, l = o - c, u = t - l;
  return u < i ? u : -1;
}
var tt = Object.defineProperty, x = (s, e, t, i) => {
  for (var o = void 0, r = s.length - 1, n; r >= 0; r--)
    (n = s[r]) && (o = n(e, t, o) || o);
  return o && tt(e, t, o), o;
};
const it = "muse-meta-item-group";
var g;
const _ = (g = class extends G {
  constructor() {
    super(...arguments), this._isOpen = !1, this._isOverflowing = !1, this._childrenLength = 0, this._isInitialized = !1, this._overflowBreakpoint = null, this._resizeTimeout = null, this._triggerHeightObserver = null, this.alwaysCollapsed = !1, this._handleEscape = (e) => {
      this._isOpen && e.key === "Escape" && this.toggle(!1);
    }, this._handleOutsideClick = (e) => {
      if (!this._isOpen)
        return;
      const t = e.composedPath();
      !t.includes(this._content) && !t.includes(this._trigger) && this.toggle(!1);
    }, this._handleResize = () => {
      this._resizeTimeout || (this._resizeTimeout = setTimeout(async () => {
        await this.updateComplete, requestAnimationFrame(() => {
          this._updateDimensions(), this._resizeTimeout = null;
        });
      }, g._RESIZE_THROTTLE));
    }, this._handleDefaultSlotChange = async () => {
      this._childrenLength = this._children.length, this._handleContenDimensionsChange();
    }, this._handleContenDimensionsChange = async () => {
      this._isInitialized = this.alwaysCollapsed, await this.updateComplete, this._isInitialized || (this._initializeSpaceManagement(), this._isInitialized = !0), this._updateDimensions();
    };
  }
  //#region -- Base
  /**
   * @ignore
   */
  static get styles() {
    return Z([Qe]);
  }
  //#endregion
  //#region -- Computed
  //#endregion
  //#region -- Lifecycles
  /**
   * @ignore
   */
  connectedCallback() {
    super.connectedCallback(), document.addEventListener("click", this._handleOutsideClick), document.addEventListener("keyup", this._handleEscape), window.addEventListener("resize", this._handleResize, { passive: !0 }), this._triggerHeightObserver = new ResizeObserver(() => {
      this._handleContenDimensionsChange();
    });
  }
  /**
   * @ignore
   */
  disconnectedCallback() {
    var e;
    document.removeEventListener("click", this._handleOutsideClick), document.removeEventListener("keyup", this._handleEscape), window.removeEventListener("resize", this._handleResize), (e = this._triggerHeightObserver) == null || e.disconnect(), super.disconnectedCallback();
  }
  /**
   * @ignore
   */
  async firstUpdated(e) {
    var t;
    super.firstUpdated(e), await this.updateComplete, this._trigger && ((t = this._triggerHeightObserver) == null || t.observe(this._trigger));
  }
  /**
   * @ignore
   */
  async updated(e) {
    super.updated(e), e.has("_isOpen") && this._isOpen && (await this.updateComplete, this._updateContentWidth(), this._updateTriggerWidth());
  }
  //#endregion
  //#region -- Methods
  /**
   * @ignore
   */
  _updateContentWidth() {
    var e, t;
    (t = this._trigger) == null || t.style.setProperty(
      "--content-width",
      `${(e = this._content) == null ? void 0 : e.getBoundingClientRect().width}px`
    );
  }
  /**
   * @ignore
   */
  _updateTriggerWidth() {
    var e, t;
    (t = this._content) == null || t.style.setProperty(
      "--trigger-width",
      `${(e = this._trigger) == null ? void 0 : e.getBoundingClientRect().width}px`
    );
  }
  /**
   * Update the css variable for trigger height
   * which sets the distance for the popover from the trigger
   *
   * @ignore
   */
  async _updateTriggerHeight() {
    var e, t;
    (t = this._content) == null || t.style.setProperty(
      "--trigger-height",
      `${(e = this._trigger) == null ? void 0 : e.getBoundingClientRect().height}px`
    );
  }
  /**
   * Update whether the space is sufficient to spread out all items
   * based on the current screen width
   *
   * @ignore
   */
  _updateIsOverflowing() {
    if (this._overflowBreakpoint === -1) {
      this._isOverflowing = !0;
      return;
    }
    this._isOverflowing = window.innerWidth <= (this._overflowBreakpoint || 0);
  }
  /**
   * Update all dimension changes
   *
   * @ignore
   */
  _updateDimensions() {
    this.alwaysCollapsed || (this._updateContentWidth(), this._updateIsOverflowing()), this._updateTriggerWidth(), this._updateTriggerHeight();
  }
  /**
   * Initialize the space management
   * by checking the needed width of all items
   * and the available space next to the item group
   *
   * @ignore
   */
  _initializeSpaceManagement() {
    var o;
    if (!this._content || ((o = this._content) == null ? void 0 : o.clientWidth) === 0)
      return;
    const e = g.CONSIDERED_MAX_SCREEN_WIDTH, t = Math.min(e, document.body.clientWidth), i = et({
      container: this._content,
      children: this._children,
      screenWidth: t,
      maxConsideredWidth: e
    });
    this._overflowBreakpoint = i > e ? -1 : i;
  }
  /**
   * Toggles the current open state if invoked without param
   *
   * @param {boolean} [force] enforce opening with `true` or closing with `false`
   */
  toggle(e = !this._isOpen) {
    this._isOpen = e;
  }
  //#endregion
  //#region -- Events
  /**
   * @ignore
   */
  _onOpen(e) {
    e.preventDefault(), this.toggle();
  }
  //#endregion
  //#region -- Render
  /**
   * @ignore
   */
  render() {
    const e = z({
      "meta-item-group": !0,
      "meta-item-group--collapsed": this.alwaysCollapsed || this._isOverflowing || this._childrenLength >= 6
    }), t = z({
      "meta-item-group__content-wrapper": !0,
      "meta-item-group__content-wrapper--initializing": !this._isInitialized,
      "meta-item-group__content-wrapper--open": this._isOpen
    }), i = z({
      "meta-item-group__trigger": !0,
      "meta-item-group__trigger--open": this._isOpen
    }), o = z({
      "meta-item-group__trigger-content": !0,
      "meta-item-group__trigger-content--open": this._isOpen
    });
    return W`
      <div class=${e}>
        <button
          id="trigger"
          class=${i}
          type="button"
          aria-expanded=${this._isOpen ? "true" : "false"}
          aria-controls="content"
          @click=${this._onOpen}
        >
          <div class=${o}>
            <slot name="trigger-icon"></slot>

            <slot name="trigger-label"></slot>

            <muse-icon
              class="meta-item-group__open-indicator"
              name=${this._isOpen ? "chevron-up" : "chevron-down"}
            ></muse-icon>
          </div>
        </button>

        <div id="content" class=${t}>
          <ul class="meta-item-group__content">
            <slot @slotchange=${this._handleDefaultSlotChange}></slot>
          </ul>
        </div>
      </div>
    `;
  }
  //#endregion
}, g.shadowRootOptions = {
  mode: "open",
  delegatesFocus: !0
}, g._RESIZE_THROTTLE = 50, g.CONSIDERED_MAX_SCREEN_WIDTH = 1328, g);
x([
  b()
], _.prototype, "_isOpen");
x([
  b()
], _.prototype, "_isOverflowing");
x([
  b()
], _.prototype, "_childrenLength");
x([
  b()
], _.prototype, "_isInitialized");
x([
  Ee({ type: Boolean, attribute: "always-collapsed" })
], _.prototype, "alwaysCollapsed");
x([
  ge("#content")
], _.prototype, "_content");
x([
  ge("#trigger")
], _.prototype, "_trigger");
x([
  ue()
], _.prototype, "_children");
let ot = _;
function lt(s) {
  Y(Ze, Ge, s), Y(M, Xe, s), Y(ot, it, s);
}
export {
  Ze as H,
  M,
  ot as a,
  lt as r
};
